/* -------------------------------------------------------------------------- */
/* Pack locale catalogs                                                        */
/*                                                                             */
/* Design text (titles, descriptions, print notes, parameter labels, help,     */
/* units, option labels, group titles, preset names, print-check messages) is  */
/* authored in English inside `defineDesign`, which is immutable once          */
/* published. Translations live next to the pack instead, one JSON file per    */
/* locale (`<pack>/locales/<locale>.json`), keyed by the English text itself:  */
/*                                                                             */
/*   designs[<designId>][<english>]  text used by one design                   */
/*   common[<english>]               text shared by several designs            */
/*   issues[<english message>]       print-check messages (exact text)         */
/*                                                                             */
/* Lookups try the design's own table, then `common`; an empty or missing      */
/* value keeps the English. Titles are matched without their "NN." ordering   */
/* prefix. `design build` copies the files to `dist/locales/`.                 */
/* -------------------------------------------------------------------------- */

/** One locale's translations for a pack (see the module comment). */
export interface PackLocaleFile {
  /** BCP 47 tag, e.g. "es-MX"; matches the file name. */
  locale: string;
  designs: Record<string, Record<string, string>>;
  common: Record<string, string>;
  issues: Record<string, string>;
}

// No zod here: this module is reachable from design bundles, which must not change when it does.
const isTable = (v: unknown): v is Record<string, string> =>
  !!v && typeof v === "object" && !Array.isArray(v) && Object.values(v).every((x) => typeof x === "string");

/** Validate a parsed catalog file; throws with the first problem. Unknown top-level keys ("$comment") are ignored. */
export function parsePackLocaleFile(json: unknown): PackLocaleFile {
  const o = (json ?? {}) as Record<string, unknown>;
  if (typeof o.locale !== "string" || !o.locale) throw new Error("pack locale file: \"locale\" must be a non-empty string");
  const designs = (o.designs ?? {}) as Record<string, unknown>;
  if (!designs || typeof designs !== "object" || Array.isArray(designs)) throw new Error("pack locale file: \"designs\" must be an object");
  for (const [id, t] of Object.entries(designs)) if (!isTable(t)) throw new Error(`pack locale file: designs["${id}"] must map strings to strings`);
  for (const k of ["common", "issues"] as const) if (o[k] !== undefined && !isTable(o[k])) throw new Error(`pack locale file: "${k}" must map strings to strings`);
  return { locale: o.locale, designs: designs as PackLocaleFile["designs"], common: (o.common ?? {}) as Record<string, string>, issues: (o.issues ?? {}) as Record<string, string> };
}

/** Translates design text for one locale; returns the English unchanged when there is no translation. */
export interface DesignTranslator {
  readonly locale: string;
  text(designId: string, english: string): string;
  /** A print-check message (exact match; messages with computed numbers stay English unless listed verbatim). */
  issue(designId: string, message: string): string;
}

const ORDER_PREFIX = /^(\s*\d+\.(?!\d)\s*)/;

/** "03. Wheel cradles" -> ["03. ", "Wheel cradles"]; titles without a prefix -> ["", title]. */
export function splitTitleOrder(title: string): [string, string] {
  const m = ORDER_PREFIX.exec(title);
  if (!m || m[1].length === title.length) return ["", title];
  return [m[1], title.slice(m[1].length)];
}

/** One translator over any number of pack catalogs for the same locale (later files win). */
export function createDesignTranslator(locale: string, files: PackLocaleFile[]): DesignTranslator {
  const designs = new Map<string, Record<string, string>>();
  const common: Record<string, string> = {};
  const issues: Record<string, string> = {};
  for (const f of files) {
    for (const [id, table] of Object.entries(f.designs)) designs.set(id, { ...designs.get(id), ...table });
    Object.assign(common, f.common);
    Object.assign(issues, f.issues);
  }
  const lookup = (designId: string, s: string) => designs.get(designId)?.[s] || common[s] || "";
  return {
    locale,
    text(designId, english) {
      if (!english) return english;
      const direct = lookup(designId, english);
      if (direct) return direct;
      const [prefix, rest] = splitTitleOrder(english);
      if (prefix) {
        const t = lookup(designId, rest);
        if (t) return prefix + t;
      }
      return english;
    },
    issue(designId, message) {
      return lookup(designId, message) || issues[message] || message;
    },
  };
}

/** The parts of a design description that carry text (worker `DesignDescription`, or a `Design`). */
export interface DescribedDesign {
  id: string;
  meta: Record<string, unknown> | object;
  ui?: Record<string, unknown> | object;
  spec: unknown[];
}

interface TextSpec {
  label?: string;
  description?: string;
  unit?: string;
  group?: string;
  options?: { value: string; label: string }[];
}
interface TextUi {
  groups?: { id: string; title: string; params?: unknown[] }[];
  presets?: { id: string; title: string; note?: string }[];
}

/** Title a parameter group gets when a param names it (`group` meta) but `ui.groups` does not list it. */
export function implicitGroupTitle(id: string): string {
  return id.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Text fields of `meta.print` (the rest are numbers and flags). */
const PRINT_TEXT = ["orientation", "material", "hardware"] as const;

/** Every translatable English string of a design, in reading order, without duplicates. */
export function designStrings(d: DescribedDesign): string[] {
  const out: string[] = [];
  const add = (s: unknown) => {
    if (typeof s === "string" && s.trim() && !out.includes(s)) out.push(s);
  };
  const meta = d.meta as Record<string, unknown>;
  add(typeof meta.title === "string" ? splitTitleOrder(meta.title)[1] : undefined);
  add(meta.description);
  add(meta.printNotes);
  const print = (meta.print ?? {}) as Record<string, unknown>;
  for (const k of PRINT_TEXT) add(print[k]);
  const ui = (d.ui ?? {}) as TextUi;
  const listed = new Set((ui.groups ?? []).map((g) => g.id));
  for (const g of ui.groups ?? []) add(g.title);
  for (const s of d.spec as TextSpec[]) {
    add(s.label);
    add(s.description);
    add(s.unit);
    for (const o of s.options ?? []) add(o.label);
    if (s.group && !listed.has(s.group)) add(implicitGroupTitle(s.group));
  }
  for (const p of ui.presets ?? []) {
    add(p.title);
    add(p.note);
  }
  return out;
}

/** A copy of the description with its text translated; ids, values and numbers are untouched. */
export function localizeDescription<T extends DescribedDesign>(d: T, tr: DesignTranslator): T {
  const t = (s: unknown) => (typeof s === "string" ? tr.text(d.id, s) : s);
  const meta = { ...(d.meta as Record<string, unknown>) };
  for (const k of ["title", "description", "printNotes"]) if (k in meta) meta[k] = t(meta[k]);
  if (meta.print && typeof meta.print === "object") {
    const print = { ...(meta.print as Record<string, unknown>) };
    for (const k of PRINT_TEXT) if (k in print) print[k] = t(print[k]);
    meta.print = print;
  }
  const ui = { ...((d.ui ?? {}) as Record<string, unknown>) } as TextUi & Record<string, unknown>;
  if (ui.groups) ui.groups = ui.groups.map((g) => ({ ...g, title: t(g.title) as string }));
  if (ui.presets) ui.presets = ui.presets.map((p) => ({ ...p, title: t(p.title) as string, ...(p.note !== undefined ? { note: t(p.note) as string } : {}) }));
  const spec = (d.spec as TextSpec[]).map((s) => ({
    ...s,
    ...(s.label !== undefined ? { label: t(s.label) } : {}),
    ...(s.description !== undefined ? { description: t(s.description) } : {}),
    ...(s.unit !== undefined ? { unit: t(s.unit) } : {}),
    ...(s.options ? { options: s.options.map((o) => ({ ...o, label: t(o.label) as string })) } : {}),
  }));
  return { ...d, meta, ...(d.ui !== undefined ? { ui } : {}), spec };
}
